import React from 'react';
import { motion } from 'framer-motion';
import { Check, ArrowRight, ArrowDown } from 'lucide-react';
interface MethodSectionProps {
  onOpenForm: () => void;
}
export function MethodSection({
  onOpenForm
}: MethodSectionProps) {
  const scrollToComparison = () => {
    document.getElementById('comparison')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  return <section id="method" className="py-24 lg:py-36 bg-white" data-id="element-328">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-329">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-330">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="inline-flex items-center px-3 py-1.5 rounded-full bg-navy/5 border border-navy/10 mb-6" data-id="element-331">
            <span className="text-navy text-xs font-bold tracking-widest uppercase" data-id="element-332">
              The Reporting Framework
            </span>
          </motion.div>
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-navy mb-6 tracking-tight leading-tight" data-id="element-333">
            Credit reporting issues are solved through visibility,
            documentation, and timing
          </motion.h2>
        </div>

        <div className="grid md:grid-cols-2 gap-8 lg:gap-12 mb-16" data-id="element-334">
          {/* Card 1: ADD */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="bg-offwhite rounded-2xl p-8 lg:p-10 border border-gray-100 shadow-sm relative overflow-hidden flex flex-col h-full" data-id="element-335">
            <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/3 w-64 h-64 bg-emerald/5 rounded-full blur-[60px] pointer-events-none" data-id="element-336"></div>

            <div className="text-sm font-bold text-emerald uppercase tracking-widest mb-6" data-id="element-337">
              01 / Positive Reporting Infrastructure
            </div>

            <h3 className="text-2xl font-bold text-navy mb-4" data-id="element-338">
              Add verified reporting activity first
            </h3>
            <p className="text-slate leading-[1.6] mb-8 flex-grow" data-id="element-339">
              Negative reporting receives most attention, but unstable files
              often fail long before disputes begin. Positive reporting
              consistency creates context for lenders, reduces volatility, and
              improves visibility into how bureau changes affect the file over
              time. This phase focuses on measurable reporting behavior, not
              quick-score tactics.
            </p>

            <ul className="space-y-4 mb-10" data-id="element-340">
              <li className="flex items-start text-navy font-medium" data-id="element-341">
                <div className="w-6 h-6 rounded-full bg-emerald/10 flex items-center justify-center mr-3 flex-shrink-0 mt-0.5" data-id="element-342">
                  <Check className="w-4 h-4 text-emerald" data-id="element-343" />
                </div>
                <span className="leading-snug" data-id="element-344">
                  Establish consistent on-time reporting activity
                </span>
              </li>
              <li className="flex items-start text-navy font-medium" data-id="element-345">
                <div className="w-6 h-6 rounded-full bg-emerald/10 flex items-center justify-center mr-3 flex-shrink-0 mt-0.5" data-id="element-346">
                  <Check className="w-4 h-4 text-emerald" data-id="element-347" />
                </div>
                <span className="leading-snug" data-id="element-348">
                  Monitor utilization across revolving accounts
                </span>
              </li>
              <li className="flex items-start text-navy font-medium" data-id="element-349">
                <div className="w-6 h-6 rounded-full bg-emerald/10 flex items-center justify-center mr-3 flex-shrink-0 mt-0.5" data-id="element-350">
                  <Check className="w-4 h-4 text-emerald" data-id="element-351" />
                </div>
                <span className="leading-snug" data-id="element-352">
                  Compare bureau reporting discrepancies weekly
                </span>
              </li>
              <li className="flex items-start text-navy font-medium" data-id="element-353">
                <div className="w-6 h-6 rounded-full bg-emerald/10 flex items-center justify-center mr-3 flex-shrink-0 mt-0.5" data-id="element-354">
                  <Check className="w-4 h-4 text-emerald" data-id="element-355" />
                </div>
                <span className="leading-snug" data-id="element-356">
                  Track FICO 8 movement across all three bureaus
                </span>
              </li>
            </ul>

            <button onClick={scrollToComparison} className="inline-flex items-center text-emerald font-bold hover:text-emerald-600 transition-colors group mt-auto" data-id="element-357">
              See top builders
              <ArrowDown className="ml-2 w-5 h-5 group-hover:translate-y-1 transition-transform" data-id="element-358" />
            </button>
          </motion.div>

          {/* Card 2: REMOVE */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.3
        }} className="bg-offwhite rounded-2xl p-8 lg:p-10 border border-gray-100 shadow-sm relative overflow-hidden flex flex-col h-full" data-id="element-359">
            <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/3 w-64 h-64 bg-red-500/5 rounded-full blur-[60px] pointer-events-none" data-id="element-360"></div>

            <div className="text-sm font-bold text-red-600 uppercase tracking-widest mb-6" data-id="element-361">
              02 / Consumer-Directed Investigation
            </div>

            <h3 className="text-2xl font-bold text-navy mb-4" data-id="element-362">
              Challenge reporting inconsistencies with evidence
            </h3>
            <p className="text-slate leading-[1.6] mb-8 flex-grow" data-id="element-363">
              Consumers have the right to dispute inaccurate or incomplete
              reporting under FCRA §1681i. Effective disputes rely on
              documentation, timelines, and bureau-specific inconsistencies
              rather than emotional language or mass-produced templates. The
              consumer reviews, approves, and submits every dispute directly.
            </p>

            <ul className="space-y-4 mb-10" data-id="element-364">
              <li className="flex items-start text-navy font-medium" data-id="element-365">
                <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center mr-3 flex-shrink-0 mt-0.5" data-id="element-366">
                  <Check className="w-4 h-4 text-red-600" data-id="element-367" />
                </div>
                <span className="leading-snug" data-id="element-368">
                  Identify mismatched balances and reporting dates
                </span>
              </li>
              <li className="flex items-start text-navy font-medium" data-id="element-369">
                <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center mr-3 flex-shrink-0 mt-0.5" data-id="element-370">
                  <Check className="w-4 h-4 text-red-600" data-id="element-371" />
                </div>
                <span className="leading-snug" data-id="element-372">
                  Archive bureau responses and correspondence
                </span>
              </li>
              <li className="flex items-start text-navy font-medium" data-id="element-373">
                <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center mr-3 flex-shrink-0 mt-0.5" data-id="element-374">
                  <Check className="w-4 h-4 text-red-600" data-id="element-375" />
                </div>
                <span className="leading-snug" data-id="element-376">
                  Compare furnisher reporting across all bureaus
                </span>
              </li>
              <li className="flex items-start text-navy font-medium" data-id="element-377">
                <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center mr-3 flex-shrink-0 mt-0.5" data-id="element-378">
                  <Check className="w-4 h-4 text-red-600" data-id="element-379" />
                </div>
                <span className="leading-snug" data-id="element-380">
                  Draft disputes using documented evidence only
                </span>
              </li>
            </ul>

            <button onClick={onOpenForm} className="inline-flex items-center text-red-600 font-bold hover:text-red-700 transition-colors group mt-auto" data-id="element-381">
              Get the free dispute guide
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-382" />
            </button>
          </motion.div>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.4
      }} className="text-center max-w-3xl mx-auto" data-id="element-383">
          <p className="text-slate font-medium italic leading-[1.6]" data-id="element-384">
            Public complaint records reveal the same reporting patterns
            appearing across thousands of consumer files — patterns most people
            only notice after they've already been denied.
          </p>
        </motion.div>
      </div>
    </section>;
}